import React, { createContext, useContext, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { getAuth } from "../redux/auth/authSlice";
import { getExceptions } from "../api/homeSiteExceptions/homeSiteExceptions";
import { getHomeSiteNotes } from "../api/homeSiteNotes/homeSiteNotesApi";
import { getAlerts } from "../api/homeSiteAlerts/homeSiteAlerts";
import { getMilestonesTasks } from "../api/homeSiteMilestones/mileStoneTasksApi";
import { getPreferences, PREF_BUYERS, PREF_REPORTS, PREF_TASKS, } from "../api/concierges/preferences";
import { useQueryClient } from "@tanstack/react-query";
import { getAllDivisions } from "../api/divisions/divisionsApi";
import { getdivisionMilestones } from "../api/divisionMileStones/divisionMileStonesApi";
import { useHomeSiteDataIndexQuery } from "../reactQuery/homeSitesDataIndex/homeSiteDataIndexQuery";
// Define the shape of the bootstrapped data
type BootStrapDataProps = {
  data: Record<string, any>;
  isLoading: boolean;
  isError: boolean;
};

// Create the context
const BootStapDataContext = createContext<BootStrapDataProps | null>(null);

// Create the provider
export const BootStrapDataProvider: React.FC<{ children: React.ReactNode }> = ({ children, }) => {
  const queryClient = useQueryClient();
  const { userInfo } = useSelector(getAuth);
  const userDivCode = useMemo(() => userInfo?.userDivCode, [userInfo]);

  // Clear the cache when the division code changes to ensure fresh data and prevent web browser memory leaks
  useMemo(() => {
    const staticKeys = new Set(["concierges", "allDivisions"]);
    const queryCache = queryClient.getQueryCache();
    if (userDivCode) {
      const liveQueriesOnScreen = queryCache.findAll();
      liveQueriesOnScreen.forEach((query) => {
        const shouldPreserve = query.queryKey.some((key) =>
          staticKeys.has(key as string)
        );
        if (!shouldPreserve) {
          queryCache.remove(query);
        }
      });
    }
  }, [userDivCode, queryClient]);

  // Data Index Query
  const dataIndex = useHomeSiteDataIndexQuery(userDivCode);

  const staticQueriesConfig = [
    {
      key: "allDivisions",
      queryKey: ["allDivisions"],
      queryFn: () => getAllDivisions({ filter: `status=true` }),
      staleTime: 1000 * 60 * 15, // 15 minutes
      gcTime: 1000 * 60 * 15, // 15 minutes
      refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
      refetchIntervalInBackground: true,
      enabled: userDivCode !== 'LNP'
    },
  ];

  const staticQueries = useQueries({
    queries: staticQueriesConfig.map(({ queryKey, queryFn, staleTime, gcTime, refetchInterval, refetchIntervalInBackground, enabled }) => ({
      queryKey,
      queryFn,
      staleTime,
      gcTime,
      refetchInterval,
      refetchIntervalInBackground,
      enabled
    })),
  });

  // Dynamic queries configuration
  const dynamicQueriesConfig = useMemo(() => [
    { key: "divisionMileStones", queryFn: () => getdivisionMilestones({ filter: `divisionCode=${userDivCode}` }) },
    { key: "divisionMileStonesReleased", queryFn: () => getdivisionMilestones({ filter: `divisionCode=${userDivCode},mode=released` }) },
    { key: "milestoneTasks", queryFn: () => getMilestonesTasks({ filter: `divisionCode=${userDivCode},completionDatenotIn(null),closeOfEscrowActualin(null)` }, { filter: `dueDatenotIn(null),saleDatenotIn(null)` }) },
    { key: "homeSiteExceptions", queryFn: () => getExceptions({ filter: `divisionCode=${userDivCode},status=true` }) },
    { key: "homeSiteNotes", queryFn: () => getHomeSiteNotes({ filter: `divisionCode=${userDivCode},status=true,noteType=homeSiteNotes` }) },
    { key: "homeSiteAlerts", queryFn: () => getAlerts({ filter: `divisionCode=${userDivCode},status=active` }) },
    { key: `preferences-${userInfo?.email}${PREF_BUYERS}`, queryFn: () => getPreferences(userInfo?.email, PREF_BUYERS) },
    { key: `preferences-${userInfo?.email}${PREF_REPORTS}`, queryFn: () => getPreferences(userInfo?.email, PREF_REPORTS) },
    { key: `preferences-${userInfo?.email}${PREF_TASKS}`, queryFn: () => getPreferences(userInfo?.email, PREF_TASKS) },

  ], [userDivCode, userInfo?.email]);

  const dynamicQueries = useQueries({
    queries: userDivCode
      ? dynamicQueriesConfig.map(({ key, queryFn }) => ({
        queryKey: [key, userDivCode],
        queryFn,
        staleTime: 1000 * 60 * 15, // 15 minutes
        gcTime: 1000 * 60 * 15, // 15 minutes
        refetchInterval: 1000 * 60 * 15, // Refetch every 15 minutes
        refetchIntervalInBackground: true,
        enabled: !!userDivCode && !!userInfo && userDivCode !== 'LNP'
      }))
      : [],
  });

  const staticResults = staticQueriesConfig.reduce(
    (acc, { key }, index) => {
      acc[key] = staticQueries[index]?.data || null;
      return acc;
    },
    {} as Record<string, any>
  );

  const dynamicResults = dynamicQueriesConfig.reduce(
    (acc, { key }, index) => {
      acc[key] = dynamicQueries[index]?.data || null;
      return acc;
    },
    {} as Record<string, any>
  );



  const allData = { ...staticResults, ...dynamicResults, ...dataIndex };
  const isLoading = staticQueries.some((query) => query.isLoading) || dynamicQueries.some((query) => query.isLoading);
  const isError = staticQueries.some((query) => query.isError) || dynamicQueries.some((query) => query.isError);

  const value = { data: allData, isLoading, isError };

  return (
    <BootStapDataContext.Provider value={value}>
      {children}
    </BootStapDataContext.Provider>
  );
};

export const useBootStapDataContext = () => {
  const context = useContext(BootStapDataContext);
  if (!context) {
    throw new Error(
      "useBootstrappedData must be used within a BootstrapDataProvider"
    );
  }
  return context;
};